import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Package, Tag, List, Loader2 } from "lucide-react";
import { CreateProductModal } from "@/components/products/CreateProductModal";
import { CreateCategoryModal } from "@/components/products/CreateCategoryModal";
import { ProductDetail } from "@/components/products/ProductDetail";
import { ProductCard } from "@/components/products/ProductCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProducts } from "@/components/products/ProductsContext";
import { Product, ProductCategory } from "@/types/product";
import { useDebounce } from "@/hooks/useDebounce";
import { useAuth } from "@/hooks/useAuth";
import { CardGridSkeleton } from "@/components/shared/CardGridSkeleton";

export default function Products() {
  const {
    products,
    categories,
    isLoading,
    categoriesLoading,
    error,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    createCategory,
    updateCategory,
    deleteCategory,
    slug,
    organizationName,
    totalItems,
    currentPage,
    totalPages,
    setCurrentPage,
  } = useProducts();

  const { can, company } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const getCurrencySymbol = () => {
    if ((company as any)?.currency) {
      return (company as any).currency;
    }
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {}
    return "$";
  };
  const currencySymbol = getCurrencySymbol();

  const filteredProducts = useMemo(
    () =>
      products.filter((product) => {
        const matchesSearch =
          product.name
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          product.sku
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          product.description
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase());

        const matchesCategory =
          selectedCategory === "all" || product.categoryId === selectedCategory;

        return matchesSearch && matchesCategory;
      }),
    [products, debouncedSearchQuery, selectedCategory],
  );

  const totalFilteredPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, setCurrentPage]);

  const handleSaveProduct = async (product: Product) => {
    try {
      if (editingProduct && editingProduct.id) {
        await updateProduct(editingProduct.id, product);
      } else {
        await createProduct(product);
      }
      setEditingProduct(null);
      setIsProductModalOpen(false);
      await fetchProducts({ page: currentPage, limit: itemsPerPage });
    } catch (error) {}
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      await deleteProduct(id);
      await fetchProducts({ page: currentPage, limit: itemsPerPage });
    } catch (error) {}
  };

  const handleSaveCategory = async (category: ProductCategory) => {
    try {
      await createCategory(category);
    } catch (error) {}
  };

  const handleUpdateCategory = async (
    id: string,
    category: ProductCategory,
  ) => {
    try {
      await updateCategory(id, category);
    } catch (error) {}
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await deleteCategory(id);
    } catch (error) {}
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || "Sin categoría";
  };

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.color || "#6b7280";
  };

  const totalValue = products.reduce((sum, p) => sum + p.unitPrice, 0);
  const productsWithRules = products.filter(
    (p) => p.rules && p.rules.length > 0,
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Productos y Servicios
          </h1>
          <p className="text-slate-600">
            Gestiona el catálogo de productos de {organizationName}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm">
              {organizationName}
            </Badge>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setIsCategoryModalOpen(true)}
              disabled={categoriesLoading || !slug}
              className={!can("products.create") ? "hidden" : ""}
            >
              {categoriesLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Tag className="h-4 w-4 mr-2" />
              )}
              Categorías
            </Button>
            {can("products.create") && (
              <Button
                onClick={() => {
                  setEditingProduct(null);
                  setIsProductModalOpen(true);
                }}
                disabled={isLoading || categoriesLoading || !slug}
                className={`bg-black hover:bg-gray-800 text-white ${!can("products.create") ? "hidden" : ""}`}
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar Producto
              </Button>
            )}
          </div>
        </div>
      </div>

      {!slug && !isLoading && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-amber-700">
            No se ha identificado la organización. Por favor, inicia sesión.
          </p>
        </div>
      )}

      {slug && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-600">Total Productos</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {totalItems}
                    </p>
                  </div>
                  <Package className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-600">
                      Con Reglas de Precio
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      {productsWithRules}
                    </p>
                  </div>
                  <List className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-600">Categorías</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {categories.filter((c) => c.id !== "").length}
                    </p>
                  </div>
                  <Tag className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-600">Valor Catálogo</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {currencySymbol}
                      {(totalValue / 1000).toFixed(1)}K
                    </p>
                  </div>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-100 text-orange-600 font-bold text-lg">
                    {currencySymbol}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {(isLoading || categoriesLoading) ? (
            <CardGridSkeleton hasHeader={true} hasMetrics={false} cardCount={8} />
          ) : (
            <>
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
                <div className="relative flex-1 w-full sm:max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Buscar productos por nombre, SKU o descripción..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                >
                  <SelectTrigger className="w-full sm:w-64">
                    <SelectValue placeholder="Todas las categorías" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorías</SelectItem>
                    {categories
                      .filter(
                        (cat) => cat.id && cat.id.trim() !== "" && cat.id !== "",
                      )
                      .map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: cat.color || "#6b7280" }}
                            />
                            {cat.name}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {paginatedProducts.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {paginatedProducts.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onEdit={(p) => {
                          setEditingProduct(p);
                          setIsProductModalOpen(true);
                        }}
                        onDelete={handleDeleteProduct}
                        onClick={(p) => {
                          setSelectedProduct(p);
                          setIsDetailOpen(true);
                        }}
                        getCategoryName={getCategoryName}
                        getCategoryColor={getCategoryColor}
                      />
                    ))}
                  </div>

                  {totalFilteredPages > 1 && (
                    <div className="flex items-center justify-between border-t pt-4 mt-6">
                      <div className="text-sm text-slate-600">
                        Mostrando {(currentPage - 1) * itemsPerPage + 1} -{" "}
                        {Math.min(
                          currentPage * itemsPerPage,
                          filteredProducts.length,
                        )}{" "}
                        de {filteredProducts.length} productos
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        >
                          Primera
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCurrentPage(Math.max(1, currentPage - 1))
                          }
                          disabled={currentPage === 1}
                        >
                          Anterior
                        </Button>

                        <div className="flex items-center gap-1">
                          {Array.from(
                            { length: Math.min(5, totalFilteredPages) },
                            (_, i) => {
                              let pageNum;
                              if (totalFilteredPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalFilteredPages - 2) {
                                pageNum = totalFilteredPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }

                              return (
                                <Button
                                  key={pageNum}
                                  variant={
                                    currentPage === pageNum ? "default" : "outline"
                                  }
                                  size="sm"
                                  onClick={() => setCurrentPage(pageNum)}
                                  className="w-8 h-8 p-0"
                                >
                                  {pageNum}
                                </Button>
                              );
                            },
                          )}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCurrentPage(
                              Math.min(totalFilteredPages, currentPage + 1),
                            )
                          }
                          disabled={currentPage === totalFilteredPages}
                        >
                          Siguiente
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(totalFilteredPages)}
                          disabled={currentPage === totalFilteredPages}
                        >
                          Última
                        </Button>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <span>Productos por página:</span>
                        <Select
                          value={itemsPerPage.toString()}
                          onValueChange={(value) => {
                            setItemsPerPage(Number(value));
                            setCurrentPage(1);
                          }}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="12">12</SelectItem>
                            <SelectItem value="24">24</SelectItem>
                            <SelectItem value="48">48</SelectItem>
                            <SelectItem value="96">96</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    No hay productos
                  </h3>
                  <p className="text-slate-600 mb-4">
                    {searchQuery || selectedCategory !== "all"
                      ? "No se encontraron productos con ese criterio"
                      : "Comienza creando tu primer producto"}
                  </p>
                  {!searchQuery && selectedCategory === "all" && (
                    <Button
                      onClick={() => setIsProductModalOpen(true)}
                      disabled={categoriesLoading || !slug}
                      className={!can("products.create") ? "hidden" : ""}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Agregar Producto
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      <CreateProductModal
        isOpen={isProductModalOpen}
        onClose={() => {
          setIsProductModalOpen(false);
          setEditingProduct(null);
        }}
        onSave={handleSaveProduct}
        categories={categories.filter((cat) => cat.id !== "")}
        editingProduct={editingProduct}
      />

      <CreateCategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => {
          setIsCategoryModalOpen(false);
        }}
        onSave={handleSaveCategory}
        onUpdate={(category) => {
          handleUpdateCategory(category.id, category);
        }}
        onDelete={(categoryId) => {
          handleDeleteCategory(categoryId);
        }}
        categories={categories}
        slug={slug}
      />

      <ProductDetail
        product={selectedProduct}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedProduct(null);
        }}
        categories={categories}
      />
    </div>
  );
}
